import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { selectFinanceScope, setFinanceScope } from "@/features/settings";
import type { FinanceScope } from "@/features/settings";
import { useGetProjectsQuery, useGetMyProjectsQuery } from "@/features/projects/api";
import { SidebarArrowUpDownIcon } from "@/shared/assets/icons";
import { PERMISSIONS } from "@/shared/consts/permissions";
import { useAppDispatch, useAppSelector, useAuth } from "@/shared/hooks";
import { Avatar, Dropdown, DropdownItem, Skeleton } from "@/shared/ui";
import { formatDateForDisplay } from "@/shared/utils";
import type { Project } from "@/features/projects/types";

interface Props {
  isCollapsed?: boolean;
  onExpand?: () => void;
}

export default function ProjectsButton({ isCollapsed, onExpand }: Props) {
  const { t } = useTranslation("Sidebar");
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const { hasPermission } = useAuth();

  const canEditProject = hasPermission(PERMISSIONS.PROJECTS.EDIT_PROJECT);
  const financeScope = useAppSelector(selectFinanceScope);
  const allProjects = useGetProjectsQuery(undefined, { skip: !canEditProject });
  const myProjects = useGetMyProjectsQuery(undefined, { skip: canEditProject });
  const { data: projects, isLoading } = canEditProject ? allProjects : myProjects;

  useEffect(
    function validateFinanceScope() {
      if (projects && typeof financeScope === "number") {
        const found = projects.some((p: Project) => p.id === financeScope);
        if (!found) {
          dispatch(setFinanceScope(null));
        }
      }
    },
    [projects, financeScope, dispatch],
  );

  const selectedProject = typeof financeScope === "number" ? (projects?.find((p: Project) => p.id === financeScope) ?? null) : null;
  const displayName =
    financeScope === "internal"
      ? t("allInternal")
      : financeScope === "external"
        ? t("allExternal")
        : selectedProject
          ? selectedProject.name
          : t("allProjects");

  function handleSelect(scope: FinanceScope) {
    dispatch(setFinanceScope(scope));
    setIsOpen(false);
  }

  function handleButtonClick() {
    if (isCollapsed) {
      onExpand?.();
    } else {
      setIsOpen((p) => !p);
    }
  }

  if (isLoading) {
    return (
      <div
        className={cn(
          "transition-[padding,gap,opacity] duration-300",
          isCollapsed ? "flex justify-center items-center p-0 h-12 w-12" : "p-2",
        )}>
        <div
          className={cn(
            "flex items-center transition-[padding,gap,opacity] duration-300",
            isCollapsed ? "justify-center" : "justify-between p-2 w-full rounded-lg surface-tertiary-fill",
          )}>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Skeleton className={cn("shrink-0 rounded-full", isCollapsed ? "w-8 h-8" : "w-8 h-8")} />
            <Skeleton
              className={cn(
                "h-4 rounded transition-[opacity,width] duration-300",
                isCollapsed ? "opacity-0 w-0" : "opacity-100 flex-1 min-w-0",
              )}
            />
          </div>
          <Skeleton
            className={cn(
              "w-4 h-4 rounded shrink-0 transition-opacity duration-300",
              isCollapsed ? "opacity-0 hidden" : "opacity-100",
            )}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "transition-[padding,gap,opacity] duration-300",
        isCollapsed ? "flex justify-center items-center p-0 h-12 w-12" : "p-2",
      )}>
      <Dropdown
        open={isOpen && !isCollapsed}
        onClose={() => setIsOpen(false)}
        width="w-full"
        direction="bottom"
        className="max-h-[336px] overflow-y-auto component-scroll-thin"
        fullWidth>
        <button
          className={cn(
            "w-full flex items-center cursor-pointer transition-[padding,gap,opacity] duration-300 border radius-sm",
            isCollapsed
              ? "justify-center border-transparent"
              : "justify-between px-3 py-2 surface-container-stroke hover:bg-grey-50 hover:dark:bg-grey-900 focus:border-grey-100 focus:dark:border-grey-700",
          )}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onClick={handleButtonClick}
          title={isCollapsed ? t("projects") : undefined}>
          <div
            className={cn(
              "flex items-center min-w-0 transition-[gap] duration-300",
              isCollapsed ? "gap-0" : "gap-2 flex-1",
            )}>
            <Avatar
              name={displayName}
              size={32}
              shape="rectangle"
              textClassName="text-xs"
              bodyClassName={cn(!isCollapsed && isFocused && "elevation-level-1")}
            />
            <p
              className={cn(
                "text-label-sm text-left content-base-primary truncate whitespace-nowrap overflow-hidden transition-[opacity,width] duration-300",
                isCollapsed ? "opacity-0 w-0" : "opacity-100 flex-1",
              )}>
              {displayName}
            </p>
          </div>
          <div
            className={cn(
              "relative content-base-secondary overflow-hidden transition-[opacity,width] duration-300",
              isCollapsed ? "opacity-0 w-0" : "opacity-100",
            )}>
            <SidebarArrowUpDownIcon size={16} color="currentColor" />
          </div>
        </button>
        <DropdownItem onClick={() => handleSelect(null)} active={financeScope === null}>
          <div className="flex items-center gap-2 min-w-0">
            <Avatar name={t("allProjects")} size={24} shape="rectangle" textClassName="text-[10px]" />
            <span className="truncate">{t("allProjects")}</span>
          </div>
        </DropdownItem>
        <DropdownItem onClick={() => handleSelect("internal")} active={financeScope === "internal"}>
          <div className="flex items-center gap-2 min-w-0">
            <Avatar name={t("allInternal")} size={24} shape="rectangle" textClassName="text-[10px]" />
            <span className="truncate">{t("allInternal")}</span>
          </div>
        </DropdownItem>
        <DropdownItem onClick={() => handleSelect("external")} active={financeScope === "external"}>
          <div className="flex items-center gap-2 min-w-0">
            <Avatar name={t("allExternal")} size={24} shape="rectangle" textClassName="text-[10px]" />
            <span className="truncate">{t("allExternal")}</span>
          </div>
        </DropdownItem>
        {projects?.map((project: Project) => (
          <DropdownItem
            key={project.id}
            onClick={() => handleSelect(project.id)}
            active={financeScope === project.id}>
            <div className="flex items-center gap-2 min-w-0">
              <Avatar name={project.name} size={24} shape="rectangle" textClassName="text-[10px]" />
              <div className="min-w-0">
                <p className="truncate">{project.name}</p>
                {(project.start_date || project.end_date) && (
                  <p className="text-body-regular-xs content-base-secondary truncate">
                    {formatDateForDisplay(project.start_date)} – {formatDateForDisplay(project.end_date)}
                  </p>
                )}
              </div>
            </div>
          </DropdownItem>
        ))}
      </Dropdown>
    </div>
  );
}
